'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentType } from 'react'
import { Send, Loader2, CheckCircle, ImagePlus, X, AlertCircle, MessageSquare, FileText, RotateCw, Copy, Check, Braces, RefreshCw, Camera, Fingerprint } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import Turnstile, { type TurnstileHandle } from '../Turnstile'
import type { PipelineStepInput } from '../PipelineStepsWidget'
import { PayloadWidget } from './PayloadWidget'
import { EntryDetailShell, type DetailBadge } from '../EntryDetail/EntryDetailShell'
import { EntryDetailPanel, detailGridCols } from '../EntryDetail/EntryDetailPanel'
import { InvestigationSidebar, type InvestigationAction } from '../InvestigationSidebar'
import { browserLoadActions } from './browserLoadActions'
import type { SibsEntry } from '../EntryDetail/SibsWidgets'
import { SearchableSelect } from './SearchableSelect'
import { useCatalog, useEntrySources } from './entriesApi'
import type { Communication } from '@shared/postui'
import { collectFingerprint } from '@/lib/fingerprint'
import { getBrowserId } from '@/lib/browserId'
import { encryptForBackend, decryptFromBackend } from '@/lib/backendCrypto'
import { trackEvent } from '@/lib/analytics'
import type { EntryDetailData, ProcessedEntry, ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from '../ProcessedEntry/DomainEntry/processedTypes'
import { ProcessedEntryTimeline } from '../ProcessedEntry/DomainEntry/ProcessedEntryTimeline'
import { DomainIntelligencePanel } from '../ProcessedEntry/DomainEntry/DomainIntelligencePanel'
import { BrowserDataPanel } from '../ProcessedEntry/DomainEntry/BrowserDataPanel'
import { ProbeRunsPanel } from '../ProcessedEntry/DomainEntry/ProbeRunsPanel'
import { LatestObtainedImagesPanel } from '../ProcessedEntry/DomainEntry/LatestObtainedImagesPanel'
import { RefreshControl } from '../BaseDashboard'
// Cross-import back into CaseEntryDetailPage.tsx, which already imports
// ActionableEvidencePanel/deriveActionableEvidence from THIS file — a
// circular module reference between the two, same as SibsMailView.tsx's own
// one-way import of CollapsibleSection. Safe here because both sides only
// ever call into the other's exports from inside a component body (render
// time), never at module-eval time, which is the usual failure mode for
// circular ESM imports.
import { CollapsibleSection } from './CaseEntryDetailPage'
import { GenericEntryRow } from './GenericEntryRow'

// A staff-facing workbench for manually registering fraud report entries.
// Standard mode is the full guided form (postType/from/subject/text/
// messageTypeId/image, Turnstile + fingerprint gated) — it can target
// /submitpost itself or any future backend endpoint built to the same
// contract (STANDARD_ENDPOINTS below). Raw mode is a bare text/image form
// aimed at a hardcoded list of other, lighter raw-ingestion endpoints
// (RAW_ENDPOINTS below). Both submit anonymously — see the submit-entry API
// route for why the operator's session cookie must never be forwarded to
// either call.

type SubmitMode = 'standard' | 'raw'

// Hardcoded on purpose — add an entry here per backend endpoint built "in the
// image of" /submitpost (same EitherOrSchemaRawEntrySchema request shape,
// same Turnstile + fingerprint gate, same anonymous-key encryption) as they
// get created. Must also be whitelisted server-side in submit-entry/route.ts's
// ALLOWED_TARGETS.
type StandardEndpoint = {
  id: string
  label: string
  path: string
}

const STANDARD_ENDPOINTS: StandardEndpoint[] = [
  { id: 'submitpost', label: 'Default (submitpost)', path: '/submitpost' },
  { id: 'submit34', label: 'Experimental (submit34 / classify_e34)', path: '/experimental/submit34' },
]

// Hardcoded on purpose — add an entry here per backend raw-ingestion route as
// they're wired up. `platform`/`messageType` are attached to every submission
// through this endpoint automatically (EitherOrSchemaRawEntrySchema allows
// omitting both, so leave them out entirely if an endpoint doesn't need one).
// Must also be whitelisted server-side in submit-entry/route.ts's ALLOWED_TARGETS.
type RawEndpoint = {
  id: string
  label: string
  path: string
  platform?: string
  messageType?: string
  requiresTurnstile?: boolean
}

const RAW_ENDPOINTS: RawEndpoint[] = [
  { id: 'ctt', label: 'CTT', path: '/cttpost', platform: 'ctt' },
]

// Default `source` for Raw-mode submissions — the value every Raw submission
// carried before the form grew its own Source field.
const RAW_DEFAULT_SOURCE = 'backoffice-raw'

const PASSWORD = process.env.NEXT_PUBLIC_ENCRYPTION_PASSWORD ?? ''
// The /submitpost-family "extension" turnstile pair — post.ts/submit34.ts
// both branch on the submitted turnstile_key: "extension" selects
// PUBLIC_CLOUDFLARE_TURNSTILE_POSTSUBMITEXTENSION_SECRET_KEY instead of the
// regular PUBLIC_CLOUDFLARE_TURNSTILE_POSTSUBMIT_SECRET_KEY the real webapp
// consumer flow uses. Workbench submissions are admin/test traffic, not
// genuine end users, so they get their own key pair (same mechanism this
// backend already has ready for a browser-extension client, just not
// consumed by one yet) — keeps them distinguishable in Cloudflare's
// Turnstile dashboard and in backend validation from real submissions.
const TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_POSTSUBMITEXTENSION_KEY ?? ''
// Separate widget/key from TURNSTILE_SITEKEY above — /api/screenshots (→
// backend /getpostimages) needs its own token, same TF_CENTER_GETENTRIES key
// every other dashboard's getScreenshotToken plumbing uses (see
// CasesDashboardView/CttMailAlertView/BackofficeApp), not the submission one.
const SCREENSHOT_TURNSTILE_SITEKEY = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_TF_CENTER_GETENTRIES_KEY ?? ''

function fileToDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// Mirrors SibsWidgets.tsx's sibsCommunication() but reads the *submitted*
// payload (raw_entries.original_payload — same camelCase shape the workbench
// itself POSTs: postType/text/from/subject) rather than a persisted entry row,
// since this is a preview of what was just sent, not an already-ingested entry.
function workbenchCommunication(payload: Record<string, unknown> | null, receivedAt: string): Communication | undefined {
  const text = typeof payload?.text === 'string' ? payload.text : undefined
  if (!text) return undefined
  const sender = typeof payload?.from === 'string' && payload.from ? payload.from : 'Unknown'
  const subject = typeof payload?.subject === 'string' ? payload.subject : undefined
  return subject
    ? { type: 'email', sender, subject, receivedAt, body: text }
    : { type: 'sms', sender, receivedAt, body: text }
}

type PreviewImage = { file: File; url: string }
type SubmitStatus = { type: 'success' | 'error'; text: string; claims?: string[] }

// Shape of GET /userentrydetail's `data.entry` — only the fields this view
// actually reads (pipeline steps + the raw_entries payload diff); the real
// response carries the full user_entries/raw_entries/processed_entries join.
type EntryDetail = {
  user_entry_pipeline_steps?: PipelineStepInput[]
  raw_entries?: {
    original_payload?: Record<string, unknown> | null
    parsed_payload?: Record<string, unknown> | null
    city_at_create_time?: string | null
    region_at_create_time?: string | null
    country_at_create_time?: string | null
    continent_at_create_time?: string | null
  } | null
  // Present on the real user_entries.* join but unused until the widget-
  // library adapter below needed a real timestamp for its synthetic SibsEntry.
  created_at?: string
}

// ─── Actionable evidence (derived from pipeline_trace) ───────────────────────
// classify_e34 emits a literal `actionable_evidence` object somewhere in
// pipeline_trace — { domains, emails, entity_references, other, phone_numbers }
// — already in the shape below. This looks specifically for that key (and
// actionable_evidence_explanation alongside it) rather than scanning
// free text for anything phone/email-shaped: an earlier, more "generous"
// version regex-scanned every string in the payload as a fallback, which
// produced false-positive phone numbers pulled from unrelated digit
// sequences elsewhere in the trace — worse than showing nothing.
type EvidenceDomain = {
  original_url?: string
  domain?: string
  create_date?: string
  registrar?: string
  is_active?: boolean
  domainNameExt?: string
}

type EvidenceEntityReference = {
  entity_number?: string
  reference?: string
  value?: string
  beneficiario?: string
  paymentowner?: string
  lookup_status?: string
}

type EvidenceOther = { type: string } & Record<string, unknown>

export type ActionableEvidence = {
  explanations: string[]
  phone_numbers: string[]
  emails: string[]
  domains: EvidenceDomain[]
  entity_references: EvidenceEntityReference[]
  other: EvidenceOther[]
}

const EVIDENCE_LIMIT = 25

function evidenceAsRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
}

function pickValue(record: Record<string, unknown>, ...keys: string[]): unknown {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) return record[key]
  }
  return undefined
}

function pickStr(record: Record<string, unknown>, ...keys: string[]): string | undefined {
  const v = pickValue(record, ...keys)
  return typeof v === 'string' && v.trim() ? v : undefined
}

function pickFlag(record: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  const v = pickValue(record, ...keys)
  return typeof v === 'boolean' ? v : undefined
}

// Lenient on purpose — actionable_evidence_explanation might come through as
// a plain string, or as a wrapper object ({text}/{explanation}/{summary}/
// {value}) depending on which trace step produced it.
function pickExplanation(record: Record<string, unknown>, ...keys: string[]): string | undefined {
  const raw = pickValue(record, ...keys)
  if (typeof raw === 'string' && raw.trim()) return raw
  const rec = evidenceAsRecord(raw)
  return rec ? pickStr(rec, 'text', 'explanation', 'summary', 'value') : undefined
}

function pickStrArray(record: Record<string, unknown>, ...keys: string[]): string[] {
  const v = pickValue(record, ...keys)
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.trim().length > 0) : []
}

function pickRecordArray(record: Record<string, unknown>, ...keys: string[]): Record<string, unknown>[] {
  const v = pickValue(record, ...keys)
  return Array.isArray(v) ? v.map(evidenceAsRecord).filter((x): x is Record<string, unknown> => x != null) : []
}

// Finds every literal `actionable_evidence` object and every
// `actionable_evidence_explanation` string anywhere in the tree — recursing
// through JSON-encoded string values too, since agentic trace output
// frequently nests one inside a field that's itself JSON text rather than an
// already-parsed object. Deliberately does NOT read any other field: no
// regex text-scanning, no synthesizing entries from unrelated keys — if the
// classifier didn't put something under `actionable_evidence`, it doesn't
// show up here.
function findActionableEvidence(node: unknown, aeObjects: Record<string, unknown>[], explanations: Set<string>, depth = 0) {
  if (node == null || depth > 8) return
  if (typeof node === 'string') {
    const trimmed = node.trim()
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        findActionableEvidence(JSON.parse(trimmed), aeObjects, explanations, depth + 1)
      } catch {
        // wasn't actually JSON — just a string that happens to start/end with brace/bracket
      }
    }
    return
  }
  if (Array.isArray(node)) {
    for (const item of node) findActionableEvidence(item, aeObjects, explanations, depth + 1)
    return
  }
  const record = evidenceAsRecord(node)
  if (!record) return

  const explanation = pickExplanation(record, 'actionable_evidence_explanation', 'actionableEvidenceExplanation')
  if (explanation) explanations.add(explanation)

  const ae = evidenceAsRecord(pickValue(record, 'actionable_evidence', 'actionableEvidence'))
  if (ae) aeObjects.push(ae)

  for (const value of Object.values(record)) findActionableEvidence(value, aeObjects, explanations, depth + 1)
}

export function deriveActionableEvidence(steps: PipelineStepInput[]): ActionableEvidence {
  const aeObjects: Record<string, unknown>[] = []
  const explanations = new Set<string>()
  for (const step of steps) {
    const jsonInfo = step.jsonInfo ?? step.json_info
    if (jsonInfo) findActionableEvidence(jsonInfo, aeObjects, explanations)
  }

  // The same actionable_evidence object can legitimately show up more than
  // once (e.g. mirrored onto both a trace step's output and the classify
  // step's top-level payload) — dedupe every category by content rather than
  // just merging, or entries like "other" double up verbatim.
  const domains = new Map<string, EvidenceDomain>()
  const entityRefs = new Map<string, EvidenceEntityReference>()
  const otherSeen = new Set<string>()
  const other: EvidenceOther[] = []
  const emails = new Set<string>()
  const phones = new Set<string>()

  for (const ae of aeObjects) {
    for (const p of pickStrArray(ae, 'phone_numbers', 'phoneNumbers')) phones.add(p)
    for (const e of pickStrArray(ae, 'emails')) emails.add(e.toLowerCase())

    for (const d of pickRecordArray(ae, 'domains')) {
      const domain = pickStr(d, 'domain', 'domainName', 'domain_name', 'hostname')
      const originalUrl = pickStr(d, 'original_url', 'originalUrl', 'url')
      const key = domain ?? originalUrl ?? JSON.stringify(d)
      if (!domains.has(key)) {
        domains.set(key, {
          original_url: originalUrl,
          domain,
          create_date: pickStr(d, 'create_date', 'createDate', 'created_at', 'createdAt'),
          registrar: pickStr(d, 'registrar'),
          is_active: pickFlag(d, 'is_active', 'isActive', 'active'),
          domainNameExt: pickStr(d, 'domainNameExt', 'domain_name_ext', 'tld', 'extension'),
        })
      }
    }

    for (const r of pickRecordArray(ae, 'entity_references')) {
      const entityNumber = pickStr(r, 'entity_number', 'entityNumber')
      const reference = pickStr(r, 'reference')
      const key = entityNumber || reference ? `${entityNumber ?? ''}:${reference ?? ''}` : JSON.stringify(r)
      if (!entityRefs.has(key)) {
        entityRefs.set(key, {
          entity_number: entityNumber,
          reference,
          value: pickStr(r, 'value', 'amount'),
          beneficiario: pickStr(r, 'beneficiario', 'beneficiary'),
          paymentowner: pickStr(r, 'paymentowner', 'paymentOwner', 'payment_owner'),
          lookup_status: pickStr(r, 'lookup_status', 'lookupStatus', 'status'),
        })
      }
    }

    for (const o of pickRecordArray(ae, 'other')) {
      const type = pickStr(o, 'type')
      if (!type) continue
      const key = JSON.stringify(o)
      if (otherSeen.has(key)) continue
      otherSeen.add(key)
      other.push({ ...o, type })
    }
  }

  return {
    explanations: Array.from(explanations),
    phone_numbers: Array.from(phones).slice(0, EVIDENCE_LIMIT),
    emails: Array.from(emails).slice(0, EVIDENCE_LIMIT),
    domains: Array.from(domains.values()).slice(0, EVIDENCE_LIMIT),
    entity_references: Array.from(entityRefs.values()).slice(0, EVIDENCE_LIMIT),
    other: other.slice(0, EVIDENCE_LIMIT),
  }
}

export function EvidenceBadgeList({ items, theme }: { items: string[]; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((item, i) => (
        <span key={`${item}-${i}`} className={`rounded border px-2 py-0.5 font-mono text-[10px] ${c.line} ${c.soft} ${c.text}`}>
          {item}
        </span>
      ))}
    </div>
  )
}

export function ActionableEvidencePanel({ evidence, theme }: { evidence: ActionableEvidence; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const isEmpty = evidence.explanations.length === 0 && evidence.phone_numbers.length === 0 && evidence.emails.length === 0 &&
    evidence.domains.length === 0 && evidence.entity_references.length === 0 && evidence.other.length === 0

  if (isEmpty) {
    return <p className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>No structured evidence extracted yet</p>
  }

  return (
    <div className="space-y-3">
      {evidence.explanations.length > 0 && (
        <div className={`space-y-2 rounded border p-3 ${c.line} ${theme === 'dark' ? 'bg-background' : 'bg-secondary'}`}>
          {evidence.explanations.map((text, i) => (
            <p key={i} className={`text-[12px] leading-6 ${c.muted}`}>{text}</p>
          ))}
        </div>
      )}
      {evidence.phone_numbers.length > 0 && (
        <div>
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Phone numbers</div>
          <EvidenceBadgeList items={evidence.phone_numbers} theme={theme} />
        </div>
      )}
      {evidence.emails.length > 0 && (
        <div>
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Emails</div>
          <EvidenceBadgeList items={evidence.emails} theme={theme} />
        </div>
      )}
      {evidence.domains.length > 0 && (
        <div>
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Domains</div>
          <div className="space-y-1.5">
            {evidence.domains.map((d, i) => (
              <div key={`${d.domain ?? d.original_url}-${i}`} className={`rounded border px-2.5 py-1.5 text-[11px] ${c.line} ${c.soft}`}>
                <div className={`font-mono ${c.text}`}>{d.domain ?? d.original_url ?? 'Unknown domain'}</div>
                <div className={`mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 ${c.faint}`}>
                  {d.registrar && <span>registrar: {d.registrar}</span>}
                  {d.create_date && <span>created: {d.create_date}</span>}
                  {d.is_active !== undefined && <span>{d.is_active ? 'active' : 'inactive'}</span>}
                  {d.domainNameExt && <span>ext: {d.domainNameExt}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {evidence.entity_references.length > 0 && (
        <div>
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Entity references</div>
          <div className="space-y-1.5">
            {evidence.entity_references.map((r, i) => (
              <div key={`${r.entity_number}-${r.reference}-${i}`} className={`rounded border px-2.5 py-1.5 text-[11px] ${c.line} ${c.soft}`}>
                <div className={`font-mono ${c.text}`}>
                  {r.entity_number ?? '—'} {r.reference && `· ${r.reference}`} {r.value && `· ${r.value}`}
                </div>
                <div className={`mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 ${c.faint}`}>
                  {r.beneficiario && <span>beneficiário: {r.beneficiario}</span>}
                  {r.paymentowner && <span>owner: {r.paymentowner}</span>}
                  {r.lookup_status && <span>status: {r.lookup_status}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {evidence.other.length > 0 && (
        <div>
          <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>Other</div>
          <div className="flex flex-wrap gap-1.5">
            {evidence.other.map((o, i) => {
              const { type, ...rest } = o
              const detail = pickStr(rest, 'value') ?? Object.values(rest).find((v) => typeof v === 'string') as string | undefined
              return (
                <span key={`${type}-${i}`} className={`rounded border px-2 py-0.5 font-mono text-[10px] ${c.line} ${c.soft} ${c.text}`}>
                  {type}{detail ? `: ${detail}` : ''}
                </span>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

function FieldLabel({ label, hint, theme }: { label: string; hint?: string; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  return (
    <div className="mb-1.5 flex items-baseline gap-1.5">
      <span className={`text-[12px] font-medium ${c.label}`}>{label}</span>
      {hint && <span className={`text-[11px] ${c.faint}`}>· {hint}</span>}
    </div>
  )
}

// Same tile recipe OverviewView/SibsMailView use for every bento-grid card —
// border overflow-hidden, border-border bg-card dark /
// border-border bg-card light — so the workbench sits visually flush with
// the rest of the dashboards instead of using watchClasses' slightly darker
// rounded-[4px] panel tone.
function tileClass(dark: boolean) {
  return `border overflow-hidden ${dark ? 'border-border bg-card' : 'border-border bg-card'}`
}

function MethodCard({ icon: Icon, title, subtitle, active, onClick, theme, className = '' }: {
  icon: ComponentType<{ className?: string }>
  title: string
  subtitle: string
  active: boolean
  onClick: () => void
  theme: ProcessedTheme
  className?: string
}) {
  const c = watchClasses(theme)
  const dark = theme === 'dark'
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col gap-2 p-4 text-left transition-colors ${className} ${
        active ? 'border border-signal bg-signal-surface/60' : `${tileClass(dark)} hover:border-signal/40`
      }`}
    >
      <Icon className={`h-4 w-4 ${active ? 'text-signal' : c.muted}`} />
      <div>
        <div className={`text-[13px] font-semibold ${c.text}`}>{title}</div>
        <div className={`text-[11px] ${c.muted}`}>{subtitle}</div>
      </div>
    </button>
  )
}

function StatusBanner({ status }: { status: SubmitStatus }) {
  const success = status.type === 'success'
  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2 text-[12px] ${
      success
        ? 'border-success/40 bg-success-surface text-success'
        : 'border-destructive/40 bg-danger-surface text-destructive'
    }`}>
      {success ? <CheckCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
      <div className="flex flex-col gap-0.5">
        <span>{status.text}</span>
        {status.claims?.map((claim, i) => <span key={i}>{claim}</span>)}
      </div>
    </div>
  )
}

function ImagePicker({ preview, onPick, onClear, onFileSelect, onFileDrop, inputRef, theme, disabled = false }: {
  preview: PreviewImage | null
  onPick: () => void
  onClear: () => void
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFileDrop: (file: File) => void
  inputRef: React.RefObject<HTMLInputElement>
  theme: ProcessedTheme
  disabled?: boolean
}) {
  const c = watchClasses(theme)
  const [isDragActive, setIsDragActive] = useState(false)

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return
    e.preventDefault()
    setIsDragActive(true)
  }
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragActive(false)
  }
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragActive(false)
    if (disabled) return
    const file = e.dataTransfer.files?.[0]
    if (file) onFileDrop(file)
  }

  return (
    <div
      className={`flex flex-col gap-1.5 rounded-md transition-colors ${isDragActive ? 'ring-2 ring-ring ring-offset-1' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {preview && (
        <div className="relative inline-block">
          <img src={preview.url} alt="Preview" className={`max-h-40 rounded border object-contain ${c.line} ${disabled ? 'opacity-60' : ''}`} />
          {!disabled && (
            <button
              onClick={onClear}
              className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-white shadow-sm"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFileSelect} disabled={disabled} />
      {!preview && !disabled ? (
        <div
          className={`flex flex-col items-center gap-1.5 rounded-md border border-dashed px-4 py-6 text-center ${
            isDragActive ? 'border-signal bg-signal-surface/60' : c.line
          }`}
        >
          <ImagePlus className={`h-4 w-4 ${c.muted}`} />
          <p className={`text-[11px] ${c.muted}`}>Drag and drop an image, paste (Ctrl+V), or</p>
          <Button type="button" size="sm" variant="outline" onClick={onPick} className={`flex items-center gap-1.5 ${c.input}`}>
            <ImagePlus className="h-3.5 w-3.5" />
            Choose image
          </Button>
        </div>
      ) : (
        <Button type="button" size="sm" variant="outline" onClick={onPick} disabled={disabled} className={`flex items-center gap-1.5 self-start ${c.input}`}>
          <ImagePlus className="h-3.5 w-3.5" />
          Change image
        </Button>
      )}
    </div>
  )
}

export function AdminSubmitEntryView({ theme }: { theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  const [mode, setMode] = useState<SubmitMode>('standard')
  // Same catalog RawProcessedEntryPanel/AssignFieldsWidget use to populate
  // their own platform SearchableSelect — backs Standard mode's own Platform
  // picker below.
  const { catalog } = useCatalog()
  // Distinct user_entries.source values recently seen in the db — the
  // options behind both modes' Source picker, which is the same
  // SearchableSelect the Platform picker uses (allowCustom, so a value
  // that's never been used before can still be typed). The Raw default is
  // always offered even before any Raw submission has actually persisted it.
  const { entrySources } = useEntrySources()
  const sourceOptions = useMemo(
    () => Array.from(new Set([RAW_DEFAULT_SOURCE, ...entrySources])).map((value) => ({ id: value, name: value })),
    [entrySources],
  )

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileHandle>(null)

  // autoRun + reset-and-retry on error/timeout mirrors OverviewView's
  // Turnstile usage — the challenge solves itself in the
  // background as soon as the widget mounts, so a token is already sitting
  // in state by the time the operator submits.
  const handleTurnstileError = useCallback(() => {
    turnstileRef.current?.reset()
    turnstileRef.current?.run()
  }, [])

  // Screenshot token plumbing — same pattern CasesDashboardView/CttMailAlertView/
  // BackofficeApp use for their own LatestObtainedImagesPanel usage. Separate
  // widget from the submission Turnstile above: this one never autoRuns, it
  // only fires (and resolves whichever call is waiting on it) when
  // LatestObtainedImagesPanel actually asks for a token.
  const screenshotTurnstileRef = useRef<TurnstileHandle>(null)
  const pendingScreenshotTokenRef = useRef<((token: string) => void) | null>(null)
  const handleScreenshotVerify = useCallback((token: string) => {
    pendingScreenshotTokenRef.current?.(token)
    pendingScreenshotTokenRef.current = null
  }, [])
  const getScreenshotToken = useCallback((): Promise<string> =>
    new Promise((resolve) => {
      pendingScreenshotTokenRef.current = resolve
      const tryRun = (attempt = 0) => {
        if (screenshotTurnstileRef.current) {
          screenshotTurnstileRef.current.reset()
          screenshotTurnstileRef.current.run()
          return
        }
        if (attempt < 20) setTimeout(() => tryRun(attempt + 1), 100)
      }
      tryRun()
    }), [])

  // ─── Entry detail side panels (pipeline steps + payload diff) ────────────
  // Shared across both modes — set after any successful submit (Standard or
  // Raw both ultimately insert via processRawEntryInsert, which now returns
  // user_entry_id on every response). Classification is frequently deferred
  // server-side (c.executionCtx.waitUntil), so this polls GET /userentrydetail
  // a few times rather than fetching once, to catch classify_success/failed —
  // and the parsed_payload it writes — landing shortly after the initial
  // raw_entry_success step.
  const PIPELINE_TERMINAL_STEPS = useRef(new Set(['classify_success', 'classify_failed', 'classify_retry_success', 'classify_retry_failed']))
  const [activeUserEntryId, setActiveUserEntryId] = useState<string | null>(null)
  const [entryDetail, setEntryDetail] = useState<EntryDetail | null>(null)
  const [entryDetailLoading, setEntryDetailLoading] = useState(false)
  // Drives RefreshControl's "updated Xm ago" label — same meaning as
  // CaseEntryDetailPage's own lastUpdated, but starts null (there's nothing
  // to have "last updated" until a submission actually lands, unlike Cases
  // which always opens on a real existing entry).
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const pipelineSteps = entryDetail?.user_entry_pipeline_steps ?? []
  const originalPayload = entryDetail?.raw_entries?.original_payload ?? null
  const parsedPayload = entryDetail?.raw_entries?.parsed_payload ?? null

  // A domain can get registered against the entry (user_entry_domain, via
  // the same pipeline step CaseEntryDetailPage's curDomain reads) even when
  // classify_e34's actionable_evidence trace doesn't mention one — the two
  // are written by different steps. /api/campaigns/ctt/entry-domains (not
  // actually CTT-specific despite the path) normally resolves this from a
  // user_entry_domain_id looked up off a Cases-list row, which Workbench
  // doesn't have; it also accepts a userEntryId directly, which Workbench
  // does have as soon as a submission lands.
  const [domainData, setDomainData] = useState<EntryDetailData | null>(null)
  // Tracks every in-flight fetchDomainData call, automatic poll ticks
  // included — not just the manual Refresh button — so "still checking"
  // is visible regardless of what triggered the fetch.
  const [domainDataLoading, setDomainDataLoading] = useState(false)
  // Surfaced in the section below so "no domain yet" (domainData has zero
  // domains, expected before anything's been probed) is visibly distinct
  // from "the fetch itself failed" (silent before this — curDomain being
  // null looked identical either way, which is exactly what made "is it
  // fetching?" unanswerable from the UI alone).
  const [domainDataError, setDomainDataError] = useState<string | null>(null)
  const fetchDomainData = useCallback(async (userEntryId: string) => {
    setDomainDataLoading(true)
    try {
      const res = await fetch(`/api/campaigns/ctt/entry-domains?userEntryId=${encodeURIComponent(userEntryId)}`)
      const result: { success: boolean; data?: EntryDetailData; reason?: string } = await res.json()
      if (result.success) {
        setDomainData(result.data ?? null)
        setDomainDataError(null)
      } else {
        setDomainData(null)
        setDomainDataError(result.reason ?? 'Request failed')
      }
    } catch (err) {
      setDomainData(null)
      setDomainDataError(err instanceof Error ? err.message : 'Request failed')
      console.log('[domain-data fetch] failed:', err)
    } finally {
      setDomainDataLoading(false)
    }
  }, [])
  // Own on-demand refresh, separate from handleRefreshEntryDetail's — that
  // one only fires from the Payload/Actionable Evidence header, easy to
  // miss as "the" way to update the Domain intelligence section below.
  const handleRefreshDomainData = useCallback(() => {
    if (!activeUserEntryId) return
    fetchDomainData(activeUserEntryId)
  }, [activeUserEntryId, fetchDomainData])
  const curDomain = domainData?.domains[0] ?? null
  // Unlike CaseEntryDetailPage (which has an authoritative domainId from a
  // Cases-list row's processed_entries_flat.user_entry_domain_id), Workbench
  // has no signal for *which* user_entry_domain row is "the" one — an
  // operator-triggered probe can register its own separate row for the same
  // entry, distinct from whatever classification already linked. Filtering
  // browser/probe data down to curDomain.id alone silently dropped rows
  // belonging to any other domain row on the same entry (the bug: a probe's
  // domain_probe_run existed in the db but curDomain had picked a different,
  // earlier row). /api/campaigns/ctt/entry-domains already scopes everything
  // to this user_entry_id, so show every row across every domain instead —
  // BrowserDataPanel/ProbeRunsPanel don't care which domain a row came from.
  const curBrowserData = domainData?.browserData ?? []
  const latestBrowser = curBrowserData.reduce<(typeof curBrowserData)[number] | null>((latest, row) =>
    !latest || new Date(row.createdAt).getTime() > new Date(latest.createdAt).getTime() ? row : latest, null)
  const curProbeRuns = [...(domainData?.probeRuns ?? [])]
    .sort((a, b) => {
      const nodeCompare = (a.probeNode ?? '').localeCompare(b.probeNode ?? '')
      if (nodeCompare !== 0) return nodeCompare
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

  // Minimal ProcessedEntry adapter — DomainIntelligencePanel/campaignCode only
  // read createdAt/headline/title/originalUrl off this, none of which this
  // synthetic Workbench entry has beyond what's already derived above.
  const processedEntry: ProcessedEntry | null = useMemo(() => {
    if (!activeUserEntryId) return null
    return {
      id: activeUserEntryId,
      createdAt: entryDetail?.created_at ?? new Date().toISOString(),
      headline: null,
      title: null,
      messageContent: typeof originalPayload?.text === 'string' ? originalPayload.text : null,
      sender: typeof originalPayload?.from === 'string' ? originalPayload.from : null,
      processedInputDescription: null,
      postType: typeof originalPayload?.postType === 'string' ? originalPayload.postType : null,
      platformNameCode: null,
      platformName: null,
      isScam: false,
      userEntryId: activeUserEntryId,
      userEntryDomainId: curDomain?.id ?? null,
      originalUrl: curDomain?.originalUrl ?? null,
    }
  }, [activeUserEntryId, entryDetail, originalPayload, curDomain])

  // Timestamped once per submission (not on every render) — it's a preview of
  // something that was, accurately, just submitted.
  const submittedAt = useMemo(
    () => new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short', timeStyle: 'short' }).format(new Date()),
    [activeUserEntryId],
  )
  const originalCommunication = useMemo(() => workbenchCommunication(originalPayload, submittedAt), [originalPayload, submittedAt])
  const actionableEvidence = useMemo(() => deriveActionableEvidence(pipelineSteps), [pipelineSteps])
  const [evidenceRawMode, setEvidenceRawMode] = useState(false)
  const [evidenceCopied, setEvidenceCopied] = useState(false)
  const handleCopyEvidence = () => {
    navigator.clipboard?.writeText(JSON.stringify(actionableEvidence, null, 2)).then(() => {
      setEvidenceCopied(true)
      setTimeout(() => setEvidenceCopied(false), 1500)
    })
  }

  // Session-only "add a widget" library (same mechanism as SIBS's entry
  // detail view) — the SIBS widgets it draws from expect a SibsEntry shape
  // this generic Workbench entry doesn't have (no entity_reference/
  // beneficiario/etc.), so everything not derivable here is nulled out
  // rather than omitted; the widgets themselves render gracefully empty for
  // missing fields instead of crashing. Also backs the "Entry Data" widget
  // below (SibsEntryDataWidget, same one CaseEntryDetailPage leads with) —
  // sender/message_content/post_type/source_type/platform_text/
  // message_type_id ARE derivable here (they're exactly what the operator
  // submitted, see mapPayloadToRawEntryColumns' own postBody→column mapping),
  // so those are read off originalPayload instead of nulled out like the
  // fields SIBS-only widgets need but a generic Workbench entry never has.
  const sibsEntryShape: SibsEntry | null = useMemo(() => {
    if (!activeUserEntryId) return null
    return {
      id: activeUserEntryId,
      user_entry_id: activeUserEntryId,
      created_at: entryDetail?.created_at ?? new Date().toISOString(),
      email_sent: false,
      entry_type: null,
      entity_reference_id: null,
      entity_reference_entity: null,
      entity_reference_reference: null,
      // Unlike the other SIBS-only fields above, a domain CAN be derivable
      // here — curDomain (from fetchDomainData/entry-domains, see its own
      // comment above) reflects the actually-registered user_entry_domain
      // row for this entry. Without this, the Entry Data widget's Domain ID
      // row showed "—" unconditionally in Workbench, even when a domain was
      // confirmed to exist in the db, same fallback CaseEntryDetailPage's
      // sibsEntry already applies via curDomain.
      domain_id: curDomain?.domainId ?? null,
      beneficiario: null,
      sender: typeof originalPayload?.from === 'string' ? originalPayload.from : null,
      message_content: typeof originalPayload?.text === 'string' ? originalPayload.text : '',
      scam_claims: null,
      platform_namelong: null,
      platform_text: typeof originalPayload?.platform === 'string' ? originalPayload.platform : null,
      post_type: typeof originalPayload?.postType === 'string' ? originalPayload.postType : null,
      // A Workbench submission is always still a fresh raw entry at this
      // point — same static 'raw_entry' label raw_entries_flat's own
      // source_type column uses for the same lifecycle stage — not derived
      // from originalPayload.source, which is the ingestion channel (below),
      // not a stage.
      source_type: 'raw_entry',
      source: typeof originalPayload?.source === 'string' ? originalPayload.source : null,
      message_type_id: typeof originalPayload?.messageTypeId === 'number' ? originalPayload.messageTypeId : null,
      message_type_text: null,
      raw_free_input: null,
      r2_key: null,
      raw_entry_original_payload: originalPayload,
      raw_entry_parsed_payload: parsedPayload,
      raw_entry_city_at_create_time: entryDetail?.raw_entries?.city_at_create_time ?? null,
      raw_entry_region_at_create_time: entryDetail?.raw_entries?.region_at_create_time ?? null,
      raw_entry_country_at_create_time: entryDetail?.raw_entries?.country_at_create_time ?? null,
      raw_entry_continent_at_create_time: entryDetail?.raw_entries?.continent_at_create_time ?? null,
      pipeline_steps: pipelineSteps.map((s) => ({
        step: s.step,
        json_info: s.json_info ?? s.jsonInfo ?? null,
        created_at: s.created_at ?? s.createdAt ?? '',
      })),
    }
  }, [activeUserEntryId, entryDetail, originalPayload, parsedPayload, pipelineSteps, curDomain])

  // ─── Investigation sidebar (same shared component + actions as
  // CaseEntryDetailPage's — see InvestigationSidebar's own comment on why
  // it's built generic). Same priority order as CaseEntryDetailPage's
  // curDomain fallback: the actually-registered domain (curDomain, above)
  // first, then a domain the classifier's actionable_evidence trace
  // mentioned (same data the Actionable Evidence panel below shows) for the
  // window before that registration lands, then the submitted payload's
  // `from` field. Sender-only would leave this permanently empty for most
  // test submissions: Standard mode's Sender field is optional and Raw mode
  // doesn't collect one at all, while the domain mentioned in the message
  // body is normally the actual thing an operator wants to probe. Operator
  // can freely retarget it either way. Only shown once there's an active
  // submission — probedomain logs its run against a real user_entry_id,
  // which doesn't exist yet before a first submit.
  const [investigationTarget, setInvestigationTargetState] = useState('')
  const targetIsAutoRef = useRef(true)
  const setInvestigationTarget = (value: string) => {
    targetIsAutoRef.current = false
    setInvestigationTargetState(value)
  }
  useEffect(() => { targetIsAutoRef.current = true }, [activeUserEntryId])
  useEffect(() => {
    if (!targetIsAutoRef.current) return
    const traceDomain = actionableEvidence.domains[0]?.domain ?? actionableEvidence.domains[0]?.original_url
    const sender = typeof originalPayload?.from === 'string' ? originalPayload.from : ''
    setInvestigationTargetState(curDomain?.originalUrl ?? traceDomain ?? sender)
  }, [activeUserEntryId, curDomain, actionableEvidence, originalPayload])

  const investigationActions: InvestigationAction[] = useMemo(() => [
    // The probe writes its domain/browser/probe-run rows in the background;
    // refetch them once the run has really ended (see browserLoadActions),
    // instead of right after the 202, when nothing is there yet.
    ...browserLoadActions(activeUserEntryId, () => { if (activeUserEntryId) fetchDomainData(activeUserEntryId) }),
    { id: 'screenshot', label: 'Screenshot', description: 'Full-page evidence capture', icon: Camera },
    { id: 'impersonation', label: 'Impersonation Analysis', description: 'Brand/logo similarity + spoofed content', icon: Fingerprint },
  ], [activeUserEntryId, fetchDomainData])

  useEffect(() => {
    if (!activeUserEntryId) return
    let cancelled = false
    let attempts = 0

    const tick = async () => {
      if (cancelled) return
      try {
        const url = `/api/entries/entry-detail?user_entry_id=${encodeURIComponent(activeUserEntryId)}`
        const res = await fetch(url)
        const result: { success: boolean; data?: { entry: EntryDetail | null } } = await res.json()
        if (cancelled) return
        const entry = result.success ? result.data?.entry ?? null : null
        setEntryDetail(entry)
        if (result.success) setLastUpdated(Date.now())
        fetchDomainData(activeUserEntryId)
        attempts += 1
        const steps = entry?.user_entry_pipeline_steps ?? []
        const reachedTerminal = steps.some(s => PIPELINE_TERMINAL_STEPS.current.has(s.step))
        if (!reachedTerminal && attempts < 8) {
          setTimeout(tick, 2000)
        } else {
          setEntryDetailLoading(false)
        }
      } catch (err) {
        console.log('[entry-detail poll] failed:', err)
        if (!cancelled) setEntryDetailLoading(false)
      }
    }

    setEntryDetail(null)
    setDomainData(null)
    setDomainDataError(null)
    setEntryDetailLoading(true)
    tick()

    return () => { cancelled = true }
  }, [activeUserEntryId, fetchDomainData])

  // On-demand single fetch — separate from the automatic post-submit poll
  // above, which gives up after 8 attempts. Backs the "Refresh" button on
  // each entry-derived widget (pipeline steps, payload, actionable evidence),
  // since classification can land after the poll window closes, or the
  // operator may just want the latest state on demand.
  const handleRefreshEntryDetail = useCallback(async () => {
    if (!activeUserEntryId) return
    setEntryDetailLoading(true)
    try {
      const url = `/api/entries/entry-detail?user_entry_id=${encodeURIComponent(activeUserEntryId)}`
      const res = await fetch(url)
      const result: { success: boolean; data?: { entry: EntryDetail | null } } = await res.json()
      setEntryDetail(result.success ? result.data?.entry ?? null : null)
      if (result.success) setLastUpdated(Date.now())
      await fetchDomainData(activeUserEntryId)
    } catch (err) {
      console.log('[entry-detail refresh] failed:', err)
    } finally {
      setEntryDetailLoading(false)
    }
  }, [activeUserEntryId, fetchDomainData])

  // ─── Standard mode ────────────────────────────────────────────────────────
  const [standardEndpointId, setStandardEndpointId] = useState(STANDARD_ENDPOINTS[0]?.id ?? '')
  const [standardPostType, setStandardPostType] = useState<'text' | 'image'>('text')
  const [standardFrom, setStandardFrom] = useState('')
  const [standardSubject, setStandardSubject] = useState('')
  // Free-text ingestion-channel string (user_entries.source / raw_entries
  // .source — same field SourceWidget edits after the fact on Cases, and
  // what the Unified Entries "Source" column shows). Optional: left blank,
  // the backend applies its own per-endpoint default ("bon" for /submitpost,
  // "experimental-submit34" for submit34), same as before this field existed.
  const [standardSource, setStandardSource] = useState('')
  const [standardText, setStandardText] = useState('')
  const [standardMessageTypeId, setStandardMessageTypeId] = useState<string | number | undefined>(undefined)
  // Same platform picker RawProcessedEntryPanel/AssignFieldsWidget use to
  // assign platform on an already-landed raw entry — Standard mode had no
  // way to attach one at all, unlike Raw mode's endpoint-hardcoded default.
  const [standardPlatformId, setStandardPlatformId] = useState<string | number | undefined>(undefined)
  const [standardPreview, setStandardPreview] = useState<PreviewImage | null>(null)
  const [standardLoading, setStandardLoading] = useState(false)
  const [standardStatus, setStandardStatus] = useState<SubmitStatus | null>(null)
  // Blocks the form once a submission succeeds — the fields stay filled
  // (showing exactly what was sent) but read-only, and the submit button is
  // replaced with a dedicated Reset, so a second click can't accidentally
  // resubmit the same content as a new entry.
  const [standardSubmitted, setStandardSubmitted] = useState(false)
  const standardFileInputRef = useRef<HTMLInputElement>(null)

  // Mode badge always shown on the result shell below; a verdict badge joins
  // it once Standard mode's submit response has actually resolved one (Raw
  // mode never gets a verdict — its target endpoints don't return is_scam).
  const shellBadges: DetailBadge[] = useMemo(() => {
    const badges: DetailBadge[] = [{ label: mode === 'standard' ? 'Standard' : 'Raw', tone: 'sky' }]
    if (mode === 'standard' && standardStatus) {
      badges.push(
        standardStatus.type === 'error'
          ? { label: 'Flagged as scam', tone: 'red' }
          : { label: 'Not flagged', tone: 'emerald' },
      )
    }
    return badges
  }, [mode, standardStatus])

  useEffect(() => {
    if (mode !== 'standard' || standardPostType !== 'image' || standardSubmitted) return
    const handler = (e: ClipboardEvent) => {
      for (const item of e.clipboardData?.items ?? []) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file) {
            setStandardPreview(prev => {
              if (prev) URL.revokeObjectURL(prev.url)
              return { file, url: URL.createObjectURL(file) }
            })
          }
          return
        }
      }
    }
    document.addEventListener('paste', handler)
    return () => document.removeEventListener('paste', handler)
  }, [mode, standardPostType, standardSubmitted])

  const handleStandardFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      setStandardPreview(prev => {
        if (prev) URL.revokeObjectURL(prev.url)
        return { file, url: URL.createObjectURL(file) }
      })
    }
    e.target.value = ''
  }

  const handleStandardFileDrop = (file: File) => {
    if (!file.type.startsWith('image/')) return
    setStandardPreview(prev => {
      if (prev) URL.revokeObjectURL(prev.url)
      return { file, url: URL.createObjectURL(file) }
    })
  }

  // Core request/response handling, split out from handleStandardSubmit below
  // so the field-building step stays readable on its own.
  const submitStandardBody = async (contentBody: Record<string, unknown>, endpointPath: string) => {
    if (!turnstileToken) {
      turnstileRef.current?.run()
      return false
    }

    setStandardLoading(true)
    setStandardStatus(null)
    const token = turnstileToken
    setTurnstileToken(null)
    turnstileRef.current?.reset()
    setTimeout(() => turnstileRef.current?.run(), 100)
    try {
      const fingerprint = await collectFingerprint()
      const browserId = getBrowserId()
      // turnstile_key: 'extension' tells post.ts/submit34.ts to validate
      // against the POSTSUBMITEXTENSION secret key pair instead of the
      // regular one — see the TURNSTILE_SITEKEY comment above.
      const body: Record<string, unknown> = { ...contentBody, turnStileToken: token, turnstile_key: 'extension', browserId, fingerprint }

      const encrypted = await encryptForBackend(body, PASSWORD)
      const res = await fetch(`/api/entries/submit-entry?target=${encodeURIComponent(endpointPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encrypted),
      })
      const result: { success: boolean; data?: string; reason?: string } = await res.json()
      if (result.success) {
        trackEvent({ event_type: 'action', event_name: 'submit_entry_report', component: 'submit_entry_form', field_1_key: 'step', field_1_value: 'success' })
        let status: SubmitStatus = { type: 'success', text: "We'll evaluate whether this is a scam." }
        if (result.data) {
          try {
            const decrypted = await decryptFromBackend<{ is_scam?: boolean; scam_claims?: string[]; user_entry_id?: string }>(result.data, PASSWORD)
            if (decrypted?.is_scam === true) {
              status = decrypted.scam_claims?.length
                ? { type: 'error', text: 'This communication appears to be fraudulent:', claims: decrypted.scam_claims }
                : { type: 'error', text: 'This communication appears to be fraudulent.' }
            } else if (decrypted?.is_scam === false) {
              status = { type: 'success', text: 'Everything indicates this is not a scam.' }
            } else {
              status = { type: 'success', text: "Couldn't assess whether this is a scam. When in doubt, take precautions." }
            }
            if (decrypted?.user_entry_id) setActiveUserEntryId(decrypted.user_entry_id)
          } catch {
            // keep the default "we'll evaluate" status
          }
        }
        setStandardStatus(status)
        return true
      } else {
        setStandardStatus({ type: 'error', text: result.reason ?? 'Could not submit the report. Please try again.' })
        trackEvent({ event_type: 'action', event_name: 'submit_entry_report', component: 'submit_entry_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: (result.reason ?? 'unknown').slice(0, 200) })
        return false
      }
    } catch (err) {
      console.log('[submitStandardBody] unhandled error:', err)
      setStandardStatus({ type: 'error', text: 'An unexpected error occurred. Please try again.' })
      trackEvent({ event_type: 'action', event_name: 'submit_entry_report', component: 'submit_entry_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: err instanceof Error ? err.message.slice(0, 200) : 'unknown' })
      return false
    } finally {
      setStandardLoading(false)
    }
  }

  const handleStandardSubmit = async () => {
    const endpoint = STANDARD_ENDPOINTS.find(ep => ep.id === standardEndpointId)
    if (!endpoint) return
    if (standardPostType === 'text' && !standardText.trim()) return
    if (standardPostType === 'image' && !standardPreview) return

    const contentBody: Record<string, unknown> = { postType: standardPostType }
    if (standardPostType === 'text') contentBody.text = standardText.trim()
    if (standardPostType === 'image' && standardPreview) contentBody.imageBase64 = await fileToDataUri(standardPreview.file)
    if (standardFrom.trim()) contentBody.from = standardFrom.trim()
    if (standardSubject.trim()) contentBody.subject = standardSubject.trim()
    if (standardSource.trim()) contentBody.source = standardSource.trim()
    if (standardMessageTypeId !== undefined && standardMessageTypeId !== '') contentBody.messageTypeId = Number(standardMessageTypeId)
    if (standardPlatformId !== undefined && standardPlatformId !== '') contentBody.platformId = Number(standardPlatformId)

    const ok = await submitStandardBody(contentBody, endpoint.path)
    if (ok) setStandardSubmitted(true)
  }

  // Unblocks the form for a new submission — leaves the endpoint selection
  // alone (same reasoning as the header Reset) and deliberately doesn't touch
  // activeUserEntryId/entryDetail, so the result panels keep showing the
  // just-submitted entry until a new submission actually replaces them.
  const handleStandardFormReset = () => {
    setStandardPostType('text')
    setStandardFrom('')
    setStandardSubject('')
    setStandardSource('')
    setStandardText('')
    setStandardMessageTypeId('')
    setStandardPlatformId(undefined)
    if (standardPreview) URL.revokeObjectURL(standardPreview.url)
    setStandardPreview(null)
    setStandardStatus(null)
    setStandardSubmitted(false)
  }

  const standardSubmitLabel = standardLoading ? 'Submitting...' : !turnstileToken ? 'Verifying...' : 'Submit Report'
  const standardDisabled = standardSubmitted || standardLoading || (standardPostType === 'text' ? !standardText.trim() : !standardPreview)

  // ─── Raw mode ─────────────────────────────────────────────────────────────
  const [rawEndpointId, setRawEndpointId] = useState(RAW_ENDPOINTS[0]?.id ?? '')
  const [rawPostType, setRawPostType] = useState<'text' | 'image'>('text')
  const [rawText, setRawText] = useState('')
  // Same free-text source as Standard mode's standardSource above. Was a
  // hard-coded 'backoffice-raw' on every Raw submission; that stays the
  // default (and the fallback when the operator clears the field, see
  // handleRawSubmit) so existing Raw traffic keeps its old label.
  const [rawSource, setRawSource] = useState(RAW_DEFAULT_SOURCE)
  const [rawPreview, setRawPreview] = useState<PreviewImage | null>(null)
  const [rawLoading, setRawLoading] = useState(false)
  const [rawStatus, setRawStatus] = useState<SubmitStatus | null>(null)
  const [rawSubmitted, setRawSubmitted] = useState(false)
  const rawFileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (mode !== 'raw' || rawPostType !== 'image' || rawSubmitted) return
    const handler = (e: ClipboardEvent) => {
      for (const item of e.clipboardData?.items ?? []) {
        if (item.type.startsWith('image/')) {
          e.preventDefault()
          const file = item.getAsFile()
          if (file) {
            setRawPreview(prev => {
              if (prev) URL.revokeObjectURL(prev.url)
              return { file, url: URL.createObjectURL(file) }
            })
          }
          return
        }
      }
    }
    document.addEventListener('paste', handler)
    return () => document.removeEventListener('paste', handler)
  }, [mode, rawPostType, rawSubmitted])

  const handleRawFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      setRawPreview(prev => {
        if (prev) URL.revokeObjectURL(prev.url)
        return { file, url: URL.createObjectURL(file) }
      })
    }
    e.target.value = ''
  }

  const handleRawFileDrop = (file: File) => {
    if (!file.type.startsWith('image/')) return
    setRawPreview(prev => {
      if (prev) URL.revokeObjectURL(prev.url)
      return { file, url: URL.createObjectURL(file) }
    })
  }

  const submitRawBody = async (contentBody: Record<string, unknown>, endpoint: RawEndpoint) => {
    if (endpoint.requiresTurnstile && !turnstileToken) {
      turnstileRef.current?.run()
      return false
    }

    setRawLoading(true)
    setRawStatus(null)
    try {
      const fingerprint = await collectFingerprint()
      const browserId = getBrowserId()
      const body: Record<string, unknown> = { ...contentBody, browserId, fingerprint }
      if (endpoint.requiresTurnstile && turnstileToken) body.turnStileToken = turnstileToken

      const encrypted = await encryptForBackend(body, PASSWORD)
      const res = await fetch(`/api/entries/submit-entry?target=${encodeURIComponent(endpoint.path)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encrypted),
      })
      const result: { success: boolean; data?: string; reason?: string } = await res.json()
      if (result.success) {
        setRawStatus({ type: 'success', text: 'Submitted.' })
        trackEvent({ event_type: 'action', event_name: 'submit_entry_report', component: 'submit_entry_form', field_1_key: 'step', field_1_value: 'success' })
        if (result.data) {
          try {
            const decrypted = await decryptFromBackend<{ user_entry_id?: string }>(result.data, PASSWORD)
            if (decrypted?.user_entry_id) setActiveUserEntryId(decrypted.user_entry_id)
          } catch {
            // no pipeline id available — the status message above still stands
          }
        }
        return true
      } else {
        setRawStatus({ type: 'error', text: result.reason ?? 'Could not submit.' })
        trackEvent({ event_type: 'action', event_name: 'submit_entry_report', component: 'submit_entry_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: (result.reason ?? 'unknown').slice(0, 200) })
        return false
      }
    } catch (err) {
      console.log('[submitRawBody] unhandled error:', err)
      setRawStatus({ type: 'error', text: 'Unexpected error.' })
      trackEvent({ event_type: 'action', event_name: 'submit_entry_report', component: 'submit_entry_form', field_1_key: 'step', field_1_value: 'error', field_2_key: 'message', field_2_value: err instanceof Error ? err.message.slice(0, 200) : 'unknown' })
      return false
    } finally {
      setRawLoading(false)
    }
  }

  const handleRawSubmit = async () => {
    const endpoint = RAW_ENDPOINTS.find(ep => ep.id === rawEndpointId)
    if (!endpoint) return
    if (rawPostType === 'text' && !rawText.trim()) return
    if (rawPostType === 'image' && !rawPreview) return

    const contentBody: Record<string, unknown> = { postType: rawPostType, source: rawSource.trim() || RAW_DEFAULT_SOURCE }
    if (endpoint.platform) contentBody.platform = endpoint.platform
    if (endpoint.messageType) contentBody.messageType = endpoint.messageType
    if (rawPostType === 'text') {
      contentBody.text = rawText.trim()
    } else if (rawPreview) {
      contentBody.imageBase64 = await fileToDataUri(rawPreview.file)
    }

    const ok = await submitRawBody(contentBody, endpoint)
    if (ok) setRawSubmitted(true)
  }

  const handleRawFormReset = () => {
    setRawPostType('text')
    setRawText('')
    setRawSource(RAW_DEFAULT_SOURCE)
    if (rawPreview) URL.revokeObjectURL(rawPreview.url)
    setRawPreview(null)
    setRawStatus(null)
    setRawSubmitted(false)
  }

  const rawDisabled = rawSubmitted || rawLoading || (rawPostType === 'text' ? !rawText.trim() : !rawPreview)

  // ─── Full reset ───────────────────────────────────────────────────────────
  // Header-level control — clears every field in both modes, both status
  // banners, and the pipeline panel back to its pre-submission placeholder.
  // Deliberately leaves mode/standardEndpointId/rawEndpointId untouched — an
  // operator resetting mid-testing is almost always about to submit another
  // one against the same endpoint, not starting over from the default.
  const handleReset = () => {
    setStandardPostType('text')
    setStandardFrom('')
    setStandardSubject('')
    setStandardSource('')
    setStandardText('')
    setStandardMessageTypeId('')
    setStandardPlatformId(undefined)
    if (standardPreview) URL.revokeObjectURL(standardPreview.url)
    setStandardPreview(null)
    setStandardStatus(null)
    setStandardSubmitted(false)

    setRawPostType('text')
    setRawText('')
    setRawSource(RAW_DEFAULT_SOURCE)
    if (rawPreview) URL.revokeObjectURL(rawPreview.url)
    setRawPreview(null)
    setRawStatus(null)
    setRawSubmitted(false)

    setActiveUserEntryId(null)
    setEntryDetail(null)
    setEntryDetailLoading(false)
    setLastUpdated(null)
    setDomainData(null)
    setDomainDataError(null)
  }

  const dark = theme === 'dark'

  return (
    <div className={`flex flex-col gap-3 p-4 ${c.shell}`}>
      <Turnstile ref={turnstileRef} sitekey={TURNSTILE_SITEKEY} onVerify={setTurnstileToken} onError={handleTurnstileError} onTimeout={handleTurnstileError} autoRun />
      <Turnstile ref={screenshotTurnstileRef} sitekey={SCREENSHOT_TURNSTILE_SITEKEY} onVerify={handleScreenshotVerify} />

      {/* ── Title row ────────────────────────────────────────────────── */}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className={`font-mono text-[10px] uppercase tracking-widest ${c.label}`}>// Administration · Workbench</div>
          <h1 className={`mt-0.5 text-xl font-semibold tracking-tight ${c.text}`}>Submit an entry</h1>
          <p className={`mt-0.5 text-[13px] ${c.label}`}>Register a report manually, or test a raw ingestion endpoint</p>
        </div>
        <button
          onClick={handleReset}
          title="Clear every field in both modes and start over"
          className={`flex h-8 shrink-0 items-center gap-1.5 border px-3 text-[12px] font-medium transition-colors hover:border-signal hover:text-signal ${c.line} ${c.muted}`}
        >
          <RotateCw className="h-3.5 w-3.5" />
          Reset
        </button>
      </div>

      {/* Submission form — Workbench-exclusive, no equivalent to standardize
          against in Cases/Unified Entries. Stays its own thing, unwrapped by
          the EntryDetailShell below. */}
      <div className="flex flex-col gap-3">

      {/* Method cards each occupy half the main column's width */}
      <div className="grid grid-cols-2 gap-3">
        <MethodCard
          icon={MessageSquare}
          title="Standard"
          subtitle="Guided intake, same shape as the public report form"
          active={mode === 'standard'}
          onClick={() => setMode('standard')}
          theme={theme}
        />
        <MethodCard
          icon={FileText}
          title="Raw"
          subtitle="Bare text or image straight to a raw endpoint"
          active={mode === 'raw'}
          onClick={() => setMode('raw')}
          theme={theme}
        />
      </div>

        <div className={`${tileClass(dark)} p-4 sm:p-5`}>
        {mode === 'standard' && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <FieldLabel label="Endpoint" theme={theme} />
                <select
                  value={standardEndpointId}
                  onChange={e => setStandardEndpointId(e.target.value)}
                  disabled={standardSubmitted}
                  className={`w-full rounded border bg-transparent px-2.5 py-2 text-[13px] disabled:opacity-60 ${c.input}`}
                >
                  {STANDARD_ENDPOINTS.map(ep => <option key={ep.id} value={ep.id}>{ep.label}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel label="Post type" theme={theme} />
                <div className="flex gap-1.5">
                  {(['text', 'image'] as const).map(pt => (
                    <Button
                      key={pt}
                      type="button"
                      size="sm"
                      variant={standardPostType === pt ? 'default' : 'outline'}
                      onClick={() => setStandardPostType(pt)}
                      disabled={standardSubmitted}
                      className={standardPostType === pt ? '' : c.input}
                    >
                      {pt === 'text' ? 'Message' : 'Image'}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <FieldLabel label="Channel" hint="optional" theme={theme} />
                {/* Same wrapping trick as Platform below — SearchableSelect
                    has no disabled prop of its own. Was a hardcoded 5-entry
                    <select> (MESSAGE_TYPES) that silently fell behind the
                    real message_types catalog; now sourced the same way
                    Platform already was. */}
                <div className={standardSubmitted ? 'pointer-events-none opacity-60' : undefined}>
                  <SearchableSelect
                    theme={theme}
                    value={standardMessageTypeId}
                    options={catalog.messageTypes}
                    placeholder="Select channel…"
                    searchPlaceholder="Search channels…"
                    onChange={(id) => setStandardMessageTypeId(id)}
                  />
                </div>
              </div>
              <div>
                <FieldLabel label="Platform" hint="optional" theme={theme} />
                {/* SearchableSelect has no disabled prop of its own (no
                    existing caller needs one) — wrapping is enough since
                    standardSubmitted already locks every other field. */}
                <div className={standardSubmitted ? 'pointer-events-none opacity-60' : undefined}>
                  <SearchableSelect
                    theme={theme}
                    value={standardPlatformId}
                    options={catalog.platforms}
                    placeholder="Select platform…"
                    searchPlaceholder="Search platforms…"
                    onChange={(id) => setStandardPlatformId(id)}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <FieldLabel label="Sender" hint="optional" theme={theme} />
                <Input
                  type="text"
                  placeholder="Phone number or email"
                  value={standardFrom}
                  onChange={e => setStandardFrom(e.target.value)}
                  disabled={standardSubmitted}
                  className={c.input}
                />
              </div>
              <div>
                <FieldLabel label="Subject" hint="optional" theme={theme} />
                <Input
                  type="text"
                  placeholder="Subject line"
                  value={standardSubject}
                  onChange={e => setStandardSubject(e.target.value)}
                  disabled={standardSubmitted}
                  className={c.input}
                />
              </div>
              <div>
                <FieldLabel label="Source" hint="optional" theme={theme} />
                {/* Same wrapping trick as Platform above. */}
                <div className={standardSubmitted ? 'pointer-events-none opacity-60' : undefined}>
                  <SearchableSelect
                    theme={theme}
                    value={standardSource || undefined}
                    options={sourceOptions}
                    allowCustom
                    placeholder="Select source…"
                    searchPlaceholder="Search or type a source…"
                    onChange={(id) => setStandardSource(id ?? '')}
                  />
                </div>
              </div>
            </div>

            <div>
              <FieldLabel label="Message body" theme={theme} />
              {standardPostType === 'text' ? (
                <Textarea
                  rows={5}
                  placeholder="Paste the full message content..."
                  value={standardText}
                  onChange={e => setStandardText(e.target.value)}
                  disabled={standardSubmitted}
                  className={`resize-none ${c.input}`}
                />
              ) : (
                <ImagePicker
                  preview={standardPreview}
                  onPick={() => standardFileInputRef.current?.click()}
                  onClear={() => { if (standardPreview) URL.revokeObjectURL(standardPreview.url); setStandardPreview(null) }}
                  onFileSelect={handleStandardFileSelect}
                  onFileDrop={handleStandardFileDrop}
                  inputRef={standardFileInputRef}
                  theme={theme}
                  disabled={standardSubmitted}
                />
              )}
            </div>

            {standardStatus && <StatusBanner status={standardStatus} />}

            <div className={`flex items-center justify-between gap-3 border-t pt-4 ${c.line}`}>
              <p className={`text-[11px] ${c.muted}`}>
                {standardSubmitted ? 'Submitted — reset to send another.' : 'Submitted anonymously, same as the public intake (Turnstile + fingerprint).'}
              </p>
              {standardSubmitted ? (
                <Button type="button" variant="outline" onClick={handleStandardFormReset} className={`flex shrink-0 items-center gap-2 ${c.input}`}>
                  <RotateCw className="h-4 w-4" />
                  Reset
                </Button>
              ) : (
                <Button type="button" onClick={handleStandardSubmit} disabled={standardDisabled} className="flex shrink-0 items-center gap-2">
                  {(standardLoading || !turnstileToken) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {standardSubmitLabel}
                </Button>
              )}
            </div>
          </div>
        )}

        {mode === 'raw' && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <FieldLabel label="Endpoint" theme={theme} />
                <select
                  value={rawEndpointId}
                  onChange={e => setRawEndpointId(e.target.value)}
                  disabled={rawSubmitted}
                  className={`w-full rounded border bg-transparent px-2.5 py-2 text-[13px] disabled:opacity-60 ${c.input}`}
                >
                  {RAW_ENDPOINTS.map(ep => <option key={ep.id} value={ep.id}>{ep.label}</option>)}
                </select>
              </div>
              <div>
                <FieldLabel label="Post type" theme={theme} />
                <div className="flex gap-1.5">
                  {(['text', 'image'] as const).map(pt => (
                    <Button
                      key={pt}
                      type="button"
                      size="sm"
                      variant={rawPostType === pt ? 'default' : 'outline'}
                      onClick={() => setRawPostType(pt)}
                      disabled={rawSubmitted}
                      className={rawPostType === pt ? '' : c.input}
                    >
                      {pt === 'text' ? 'Text' : 'Image'}
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <FieldLabel label="Source" hint={`defaults to ${RAW_DEFAULT_SOURCE}`} theme={theme} />
                <div className={rawSubmitted ? 'pointer-events-none opacity-60' : undefined}>
                  <SearchableSelect
                    theme={theme}
                    value={rawSource || undefined}
                    options={sourceOptions}
                    allowCustom
                    placeholder={RAW_DEFAULT_SOURCE}
                    searchPlaceholder="Search or type a source…"
                    onChange={(id) => setRawSource(id ?? '')}
                  />
                </div>
              </div>
            </div>

            <div>
              <FieldLabel label="Message body" theme={theme} />
              {rawPostType === 'text' ? (
                <Textarea
                  rows={5}
                  placeholder="Raw text content..."
                  value={rawText}
                  onChange={e => setRawText(e.target.value)}
                  disabled={rawSubmitted}
                  className={`resize-none ${c.input}`}
                />
              ) : (
                <ImagePicker
                  preview={rawPreview}
                  onPick={() => rawFileInputRef.current?.click()}
                  onClear={() => { if (rawPreview) URL.revokeObjectURL(rawPreview.url); setRawPreview(null) }}
                  onFileSelect={handleRawFileSelect}
                  onFileDrop={handleRawFileDrop}
                  inputRef={rawFileInputRef}
                  theme={theme}
                  disabled={rawSubmitted}
                />
              )}
            </div>

            {rawStatus && <StatusBanner status={rawStatus} />}

            <div className={`flex items-center justify-between gap-3 border-t pt-4 ${c.line}`}>
              <p className={`text-[11px] ${c.muted}`}>
                {rawSubmitted ? 'Submitted — reset to send another.' : 'No guided steps — sent as-is to the selected endpoint.'}
              </p>
              {rawSubmitted ? (
                <Button type="button" variant="outline" onClick={handleRawFormReset} className={`flex shrink-0 items-center gap-2 ${c.input}`}>
                  <RotateCw className="h-4 w-4" />
                  Reset
                </Button>
              ) : (
                <Button type="button" onClick={handleRawSubmit} disabled={rawDisabled} className="flex shrink-0 items-center gap-2">
                  {rawLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {rawLoading ? 'Submitting...' : 'Submit'}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      </div>

      {/* Result view — standardized onto Cases/Unified Entries' actual
          structure (EntryDetailShell + CollapsibleSection + EntryDetailPanel
          + RefreshControl), not just restyled to look similar. Only rendered
          once a submission actually exists — sibsEntryShape/processedEntry
          are both derived from activeUserEntryId and stay null until then. */}
      {activeUserEntryId && sibsEntryShape && processedEntry && (
        <EntryDetailShell
          mode="page"
          open
          theme={theme}
          cols={4}
          title="Submitted entry"
          subtitle={submittedAt}
          badges={shellBadges}
          onClose={handleReset}
          headerActions={
            <RefreshControl
              theme={theme}
              lastUpdated={lastUpdated}
              onRefresh={handleRefreshEntryDetail}
              refreshing={entryDetailLoading}
              storageKey="workbench-entry-detail"
            />
          }
          sidebar={
            <InvestigationSidebar
              theme={theme}
              target={investigationTarget}
              onTargetChange={setInvestigationTarget}
              actions={investigationActions}
            />
          }
        >
          {/* The shared Generic row (GenericEntryRow). Workbench extras: the
              "checking…" badge while the entry detail reloads, and Actionable
              Evidence's raw-JSON toggle + copy (not on Cases/SIBS). */}
          <GenericEntryRow
            theme={theme}
            entry={sibsEntryShape}
            catalog={catalog}
            steps={pipelineSteps}
            stepsReady={entryDetail != null}
            stepsLoading={entryDetailLoading}
            stepsEmptyMessage={!activeUserEntryId ? 'Waiting for a submission' : 'No pipeline steps recorded'}
            onRefresh={handleRefreshEntryDetail}
            refreshing={entryDetailLoading}
            submission={{
              r2Key: null,
              getToken: async () => '',
              communication: originalCommunication,
              postType: originalPayload && typeof originalPayload.postType === 'string' ? originalPayload.postType : undefined,
            }}
            preview={{ raw: sibsEntryShape, processed: null, source: sibsEntryShape.source, userEntryId: activeUserEntryId ?? undefined }}
            payload={<PayloadWidget originalPayload={originalPayload} parsedPayload={parsedPayload} theme={theme} />}
            busyBadge={entryDetailLoading ? (
              <span className={`flex items-center gap-1.5 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.soft} ${c.muted}`}>
                <Loader2 className="h-3 w-3 animate-spin" />
                checking…
              </span>
            ) : undefined}
            evidenceHeaderExtras={
              <>
                <button
                  type="button"
                  onClick={() => setEvidenceRawMode((v) => !v)}
                  title={evidenceRawMode ? 'Show formatted view' : 'Show raw JSON'}
                  className={`rounded p-1 transition-colors hover:text-signal ${evidenceRawMode ? 'text-signal' : c.faint}`}
                >
                  <Braces className="h-3 w-3" />
                </button>
                <button
                  type="button"
                  onClick={handleCopyEvidence}
                  title="Copy actionable evidence as JSON"
                  className={`rounded p-1 transition-colors hover:text-signal ${c.faint}`}
                >
                  {evidenceCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </button>
              </>
            }
            evidenceBody={evidenceRawMode ? (
              <pre className={`whitespace-pre-wrap break-words rounded border p-3 font-mono text-[11px] leading-relaxed ${c.line} bg-secondary text-foreground`}>
                {JSON.stringify(actionableEvidence, null, 2)}
              </pre>
            ) : undefined}
          />

          {/* Results of an investigation-sidebar Browser Load run — same
              panel set CaseEntryDetailPage's own "Domain intelligence"
              section shows, reading the same persisted rows
              (entry_browser_data, domain_probe_run) rather than the raw
              per-click response the sidebar's own run history already
              displays. Unlike Cases (which only ever renders this once a
              domain is already known from a list row), Workbench fetches
              domain data live mid-session — so unlike Cases, this stays
              visible with explicit loading/error/empty states instead of
              just disappearing whenever there's nothing to show yet. */}
          <CollapsibleSection label="Domain intelligence" theme={theme} defaultOpen>
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className={`font-mono text-[10px] uppercase tracking-widest ${c.faint}`}>
                Fetched from user_entry_domain / entry_browser_data / domain_probe_run
              </span>
              <div className="flex shrink-0 items-center gap-1.5">
                {domainDataLoading && (
                  <span className={`flex items-center gap-1.5 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${c.line} ${c.soft} ${c.muted}`}>
                    <Loader2 className="h-3 w-3 animate-spin" />
                    checking…
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleRefreshDomainData}
                  disabled={domainDataLoading}
                  title="Refresh domain intelligence, browser data, and probe runs"
                  className={`flex items-center gap-1.5 rounded border px-2 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors hover:text-signal disabled:opacity-50 ${c.line} ${c.faint}`}
                >
                  <RefreshCw className={`h-3 w-3 ${domainDataLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
            <div className={`grid gap-3 ${detailGridCols(3)}`}>
              {domainDataError ? (
                <div className="col-span-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-danger-surface px-3 py-2 text-[12px] text-destructive">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>Couldn't load domain intelligence: {domainDataError}</span>
                </div>
              ) : curDomain ? (
                <>
                  <ProcessedEntryTimeline entry={processedEntry} domain={curDomain} browserData={curBrowserData} theme={theme} />
                  <LatestObtainedImagesPanel
                    entryId={processedEntry.id}
                    userEntryDomainId={curDomain.id}
                    latestBrowser={latestBrowser}
                    theme={theme}
                    getToken={getScreenshotToken}
                  />
                  <DomainIntelligencePanel entry={processedEntry} domain={curDomain} theme={theme} />
                  <BrowserDataPanel curBrowserData={curBrowserData} latestBrowser={latestBrowser} theme={theme} />
                  <ProbeRunsPanel curProbeRuns={curProbeRuns} theme={theme} />
                </>
              ) : (
                <div className={`col-span-3 border px-4 py-6 text-center ${c.line} ${c.panel}`}>
                  <p className={`font-mono text-[11px] uppercase tracking-widest ${c.faint}`}>
                    {domainDataLoading ? 'Checking for a registered domain…' : 'No domain registered on this entry yet'}
                  </p>
                  {!domainDataLoading && (
                    <p className={`mt-1 text-[12px] ${c.muted}`}>
                      Trigger Browser Load in the investigation sidebar, or Refresh above if one should have landed already.
                    </p>
                  )}
                </div>
              )}
            </div>
          </CollapsibleSection>
        </EntryDetailShell>
      )}

    </div>
  )
}
